import { useState, useEffect, useCallback, useRef } from "react";
import { getPaginatedQuestions } from "../../api/Question";
import { getAllLessons } from "../../api/lesson";
import AddQuestionForm from "../Forms/addQuestionForm";
import Loading from "../loading";
import GetAdmin from "../../utils/getAdmin";
import NotFoundPage from "./404";
import Header from "../Header";
import "../../assets/fonts.css";
import Navigation from "../Navigation";
import QuestionTable from "../tables/QuestionTable";
import Notification from "../Notification";

const Question = () => {
  const [loading, setLoading] = useState(true);
  const [adminData, setAdminData] = useState(null);
  const [questionData, setQuestionData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [showNotification, setShowNotification] = useState(false);
  const [status, setStatus] = useState();
  const [refresh, setRefresh] = useState(false);
  const [edit, setEdit] = useState(false);
  const [questionId, setQuestionId] = useState(null);

  // State فیلترها
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ grade: "", book: "" });
  const [lesson, setLesson] = useState("");
  const [page, setPage] = useState(1);

  // State لیست دروس
  const [allLessons, setAllLessons] = useState([]);
  const [filteredLessons, setFilteredLessons] = useState([]);

  // Ref برای جلوگیری از اجرای همزمان
  const isFirstRender = useRef(true);

  // دریافت لیست دروس
  useEffect(() => {
    const fetchLessons = async () => {
      try {
        const res = await getAllLessons();
        setAllLessons(res.data.lessons || []);
      } catch (err) {
        console.error("خطا در دریافت دروس:", err);
      }
    };
    fetchLessons();
  }, []);

  // فیلتر دروس بر اساس پایه و کتاب
  useEffect(() => {
    if (filters.grade && filters.book) {
      const filtered = allLessons.filter(
        (lessonItem) =>
          lessonItem.grade === filters.grade &&
          lessonItem.book === filters.book,
      );
      setFilteredLessons(filtered);
    } else {
      setFilteredLessons([]);
    }
    setLesson("");
  }, [filters.grade, filters.book, allLessons]);

  // تابع واکشی داده‌ها
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit: 10,
        search,
        grade: filters.grade,
        book: filters.book,
        lesson: lesson,
      };

      const [questionsRes, admin] = await Promise.all([
        getPaginatedQuestions(params),
        GetAdmin(),
      ]);

      setQuestionData(questionsRes.questions || []);
      setTotalPages(questionsRes.totalPages || 1);
      setAdminData(admin);
    } catch (err) {
      console.error("Error fetching data:", err);
      setQuestionData([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [page, search, filters.grade, filters.book, lesson]);

  // اجرای fetchData با هر تغییر در فیلترها (با debounce برای جستجو)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const timer = setTimeout(() => {
      fetchData();
    }, 500);

    return () => clearTimeout(timer);
  }, [search, fetchData]);

  // برای تغییرات فیلترهای دیگر (پایه، کتاب، درس) بدون تاخیر
  useEffect(() => {
    if (isFirstRender.current) {
      return;
    }
    fetchData();
  }, [filters.grade, filters.book, lesson, page, fetchData]);

  // توابع تغییر فیلترها (با تنظیم صفحه به ۱)
  const handleGradeChange = (value) => {
    setFilters((prev) => ({ ...prev, grade: value }));
    setPage(1);
  };

  const handleBookChange = (value) => {
    setFilters((prev) => ({ ...prev, book: value }));
    setPage(1);
  };

  const handleLessonChange = (value) => {
    setLesson(value);
    setPage(1);
  };

  const handleSearchChange = (value) => {
    setSearch(value);
    setPage(1);
  };

  // نمایش لودینگ
  if (loading && isFirstRender.current) {
    return <Loading />;
  }

  if (!adminData) {
    return null;
  }

  // پیام‌های نوتیفیکیشن
  const getNotificationProps = () => {
    switch (status) {
      case 201:
        return {
          message: "ساخت سوال موفقیت آمیز بود",
          type: "success",
          duration: 3000,
        };
      case 200:
        if (edit) {
          return {
            message: "ویرایش سوال موفقیت آمیز بود",
            type: "success",
            duration: 3000,
          };
        }
        return {
          message: "حذف سوال موفقیت آمیز بود",
          type: "success",
          duration: 3000,
        };
      case 400:
        return {
          message: "خطا : تمام موارد خواسته شده را با دقت تکمیل کنید",
          type: "error",
        };
      case 500:
        return {
          message: "مشکل سرور لطفاً دقایقی دیگر امتحان کنید",
          type: "error",
        };
      case 502:
        return {
          message: "مشکل برنامه به سازنده برنامه اطلاع دهید",
          type: "error",
        };
      case 401:
        return {
          message: "مشکل احراز هویت خروج کنید و دوباره وارد شوید",
          type: "error",
        };
      case 403:
        return {
          message: "این قابلیت برای شما فعال نیست",
          type: "info",
        };
      case 404:
        return {
          message: "درس مورد نظر پیدا نشد",
          type: "info",
        };
      case 508:
        return {
          message: "درس انتخاب نشده است",
          type: "error",
        };
      default:
        return null;
    }
  };

  const notificationProps = getNotificationProps();

  if (adminData.role === "manager" || adminData.role === "questionMaker") {
    return (
      <>
        <div className="w-full flex justify-center flex-col items-center">
          {showNotification && (
            <Notification
              {...notificationProps}
              setShowNotification={setShowNotification}
              showNotification={showNotification}
            />
          )}

          <Header adminName={adminData.name} />

          <div className="flex flex-col md:flex-row gap-6 justify-between w-full">
            <div className="w-full md:w-1/4 mb-6 lg:mb-0">
              <Navigation />
            </div>
            <div className="w-full md:w-3/4">
              <div className="w-full flex col-span-4 flex-col gap-4 shadow-lg bg-white rounded-lg">
                <AddQuestionForm
                  setStatus={setStatus}
                  setShowNotification={setShowNotification}
                  setRefresh={() => setRefresh((prev) => !prev)}
                  edit={edit}
                  setEdit={setEdit}
                  questionId={questionId}
                  questionData={questionData}
                />
              </div>
            </div>
          </div>

          {/* بخش فیلترها */}
          <div className="w-full flex flex-col md:flex-row flex-wrap gap-4 items-center justify-between px-4 bg-white rounded-lg shadow-lg py-3 mt-4">
            <input
              type="text"
              placeholder="جستجو براساس متن سوال..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full md:w-1/4 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#075B4A] focus:border-transparent outline-none"
            />

            <select
              value={filters.grade}
              onChange={(e) => handleGradeChange(e.target.value)}
              className="w-full md:w-1/5 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#075B4A] focus:border-transparent outline-none"
            >
              <option value="">همه پایه‌ها</option>
              <option value="1">اول</option>
              <option value="2">دوم</option>
              <option value="3">سوم</option>
              <option value="4">چهارم</option>
              <option value="5">پنجم</option>
              <option value="6">ششم</option>
            </select>

            <select
              value={filters.book}
              onChange={(e) => handleBookChange(e.target.value)}
              className="w-full md:w-1/5 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#075B4A] focus:border-transparent outline-none"
            >
              <option value="">همه کتاب‌ها</option>
              <option value="1">ریاضی</option>
              <option value="2">فارسی و نگارش</option>
              <option value="3">علوم</option>
              <option value="4">قرآن</option>
              <option value="5">املا</option>
              <option value="6">هدیه‌های آسمانی</option>
              <option value="7">مطالعات اجتماعی</option>
            </select>

            <select
              value={lesson}
              onChange={(e) => handleLessonChange(e.target.value)}
              className="w-full md:w-1/5 px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-[#075B4A] focus:border-transparent outline-none"
              disabled={!filters.grade || !filters.book}
            >
              <option value="">همه دروس</option>
              {filteredLessons.map((lessonItem) => (
                <option key={lessonItem._id} value={lessonItem.title}>
                  {lessonItem.title}
                </option>
              ))}
            </select>

            <button
              onClick={() => {
                setSearch("");
                setFilters({ grade: "", book: "" });
                setLesson("");
                setPage(1);
              }}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 transition-all"
            >
              پاک کردن فیلترها
            </button>
          </div>

          {/* جدول سوالات */}
          <QuestionTable
            setRefresh={setRefresh}
            data={questionData}
            setShowNotification={setShowNotification}
            setStatus={setStatus}
            setEdit={setEdit}
            setQuestionId={setQuestionId}
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      </>
    );
  } else {
    return <NotFoundPage />;
  }
};

export default Question;
